// PDF-отчёт по смене (React-PDF). Z-отчёт: шапка, касса и движения,
// ключевые показатели, оплаты, разделы, список заказов.
import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import type { ShiftDetail } from '@/services/admin';

// Регистрируем Inter с поддержкой кириллицы (файлы лежат в /public/fonts/).
Font.register({
  family: 'Inter',
  fonts: [
    { src: '/fonts/Inter-Regular.ttf' },
    { src: '/fonts/Inter-Bold.ttf', fontWeight: 700 },
  ],
});
// React-PDF по умолчанию переносит слова по английским правилам — кириллица
// рвётся посередине («Налич-ные»). Переносы отключаем: ячейки достаточно широкие.
Font.registerHyphenationCallback((word) => [word]);

const C = {
  text: '#0f172a',
  muted: '#64748b',
  softMuted: '#94a3b8',
  line: '#e2e8f0',
  softLine: '#f1f5f9',
  zebra: '#fafbfc',
  headerBg: '#f1f5f9',
  brand: '#4f62e0',
  success: '#16a34a',
  successBg: '#ecfdf5',
  danger: '#dc2626',
  dangerBg: '#fef2f2',
  warning: '#ea580c',
  warningBg: '#fff7ed',
  cashBox: '#f8fafc',
};

const s = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 46,
    paddingHorizontal: 32,
    fontFamily: 'Inter',
    fontSize: 9.5,
    color: C.text,
    lineHeight: 1.35,
  },

  // Шапка
  headerBlock: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: C.line,
    paddingBottom: 10,
    marginBottom: 12,
  },
  h1: { fontSize: 17, fontWeight: 700, marginBottom: 2 },
  subtitle: { color: C.muted, fontSize: 9.5 },

  // Секции
  h2: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 6,
    marginTop: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    color: C.muted,
  },

  muted: { color: C.muted, fontSize: 9 },

  // KV-строка (label справа, value слева)
  kvRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  kvRowBordered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: C.softLine,
  },
  kvLabel: { color: C.muted, fontSize: 9 },
  kvValue: { fontSize: 9.5 },
  kvValueStrong: { fontSize: 10, fontWeight: 700 },

  // Касса — коробка расчёта
  cashBox: {
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 6,
    padding: 10,
    backgroundColor: C.cashBox,
    marginTop: 4,
  },
  cashGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cashCell: {
    width: '33.33%',
    paddingVertical: 4,
    paddingRight: 8,
  },
  cashCellLabel: { fontSize: 8, color: C.muted, marginBottom: 1, textTransform: 'uppercase', letterSpacing: 0.2 },
  cashCellValue: { fontSize: 11, fontWeight: 700 },

  // Ключевые показатели
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 2,
  },
  statBox: {
    width: '25%',
    paddingRight: 6,
    paddingBottom: 6,
  },
  statInner: {
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 6,
    padding: 8,
  },
  statLabel: { fontSize: 8, textTransform: 'uppercase', color: C.muted, marginBottom: 2, letterSpacing: 0.2 },
  statValue: { fontSize: 11, fontWeight: 700 },

  // Таблицы
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.headerBg,
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.softLine,
    minHeight: 16,
  },
  tableRowZebra: {
    backgroundColor: C.zebra,
  },
  th: { fontWeight: 700, fontSize: 8, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.2 },
  td: { fontSize: 9 },

  right: { textAlign: 'right' },
  center: { textAlign: 'center' },

  // Двухколоночный лэйаут
  twoCol: { flexDirection: 'row', gap: 14 },
  col: { flex: 1 },

  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 20,
    fontSize: 8,
    color: C.muted,
    borderTopWidth: 0.5,
    borderTopColor: C.line,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  // Ячейки цветных значений
  posValue: { color: C.success },
  negValue: { color: C.danger },
  warnValue: { color: C.warning },

  pillBase: {
    fontSize: 8,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 8,
  },
});

const paymentLabel: Record<string, string> = {
  CASH: 'Наличные',
  CARD: 'Карта',
  TRANSFER: 'Перевод',
  QRCODE: 'QR-код',
  MIXED: 'Смешанная',
  ONLINE: 'Онлайн',
};
const statusLabel: Record<string, string> = {
  NEW: 'Новый',
  IN_PROGRESS: 'В работе',
  READY: 'Готов',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};
const sectionLabel: Record<string, string> = { KITCHEN: 'Кухня', BAR: 'Бар', NONE: 'Блюда' };
const sourceLabel: Record<string, string> = { CASH: 'Касса', APP: 'Приложение' };

// Деньги: копейки → рубли. Целые суммы без дробной части («1 200 ₽»), с копейками
// — всегда две цифры («1 200,50 ₽»), чтобы колонки сумм не «плясали».
function formatRub(kop: number): string {
  const safe = Number.isFinite(kop) ? Math.round(kop) : 0;
  const hasKop = safe % 100 !== 0;
  const rub = (safe / 100).toLocaleString('ru-RU', {
    minimumFractionDigits: hasKop ? 2 : 0,
    maximumFractionDigits: hasKop ? 2 : 0,
  });
  return `${rub} \u20BD`;
}

// Время в отчёте — по часовому поясу заведения (п.19): владелец может открыть
// отчёт из другого региона, а смена должна читаться «как на кассе».
function safeTimeZone(tz: string | undefined): string | undefined {
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}
function makeFormatters(tz: string | undefined) {
  const timeZone = safeTimeZone(tz);
  const dt = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone,
  });
  const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone });
  return {
    formatDT: (iso: string | null): string => {
      if (!iso) return '—';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? '—' : dt.format(d);
    },
    formatTime: (iso: string): string => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? '—' : time.format(d);
    },
  };
}
function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} ч` : `${h} ч ${m} мин`;
}

/** «Со списанием, за счёт официанта Иван · «Уронили»» — снятие с телефона официанта. */
function removalReasonLine(c: NonNullable<ShiftDetail['itemChanges']>[number]): string {
  const parts: string[] = [];
  if (c.reason === 'WRITE_OFF') {
    parts.push(
      c.writeOffPayer === 'WAITER'
        ? `Со списанием, за счёт официанта${c.chargedWaiterName ? ` ${c.chargedWaiterName}` : ''}`
        : 'Со списанием, за счёт заведения',
    );
  } else {
    parts.push('Без списания');
  }
  if (c.comment) parts.push(`«${c.comment}»`);
  return parts.join(' · ');
}

export function ShiftReportPdf({ shift }: { shift: ShiftDetail }) {
  const { formatDT, formatTime } = makeFormatters(shift.establishment?.timezone);
  const generatedAt = formatDT(new Date().toISOString());
  const establishmentName = shift.establishment?.name ?? 'Resstro SAS';
  const cash = shift.cash;
  // Строки Z-отчёта: закуп/расходы из кассы показываем всегда (даже 0) —
  // кассир и владелец видят одну и ту же формулу «должно быть в ящике».
  const cashPayouts = cash.cashPayouts ?? 0;
  const cashExpenses = cash.cashExpenses ?? 0;
  const transferPayouts = cash.transferPayouts ?? 0;
  const transferExpenses = cash.transferExpenses ?? 0;

  const footer = (
    <View style={s.footer} fixed>
      <Text>
        {establishmentName} · Смена #{shift.number} · Сформирован {generatedAt}
      </Text>
      <Text render={({ pageNumber, totalPages }) => `Страница ${pageNumber} из ${totalPages}`} />
    </View>
  );

  const diffColor =
    cash.diff == null
      ? C.text
      : cash.diff === 0
        ? C.success
        : cash.diff > 0
          ? C.warning
          : C.danger;

  const diffLabel =
    cash.diff == null ? '—' : cash.diff === 0 ? formatRub(0) : `${cash.diff > 0 ? '+' : ''}${formatRub(cash.diff)}`;

  return (
    <Document title={`Отчёт по смене #${shift.number}`} author="Resstro SAS">
      <Page size="A4" style={s.page}>
        {/* ── Шапка ───────────────────────────────────── */}
        <View style={s.headerBlock}>
          <View style={{ maxWidth: '70%' }}>
            <Text style={s.h1}>Отчёт по смене #{shift.number}</Text>
            <Text style={s.subtitle}>
              {establishmentName}
              {shift.establishment?.address ? ` · ${shift.establishment.address}` : ''}
            </Text>
            {shift.establishment?.phone && (
              <Text style={s.muted}>Тел.: {shift.establishment.phone}</Text>
            )}
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 9.5, fontWeight: 700 }}>
              {shift.status === 'OPEN' ? 'Смена открыта' : 'Смена закрыта'}
            </Text>
            <Text style={s.muted}>Сформирован: {generatedAt}</Text>
          </View>
        </View>

        {/* ── Общая информация + кассир ────────────────── */}
        <View style={s.twoCol}>
          <View style={s.col}>
            <Text style={s.h2}>Общая информация</Text>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Открытие</Text>
              <Text style={s.kvValue}>{formatDT(shift.openedAt)}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Закрытие</Text>
              <Text style={s.kvValue}>{formatDT(shift.closedAt)}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Длительность</Text>
              <Text style={s.kvValue}>{formatDuration(shift.durationMin)}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Кассир</Text>
              <Text style={s.kvValue}>
                {shift.cashierName ??
                  shift.cashier?.name ??
                  shift.cashier?.phone ??
                  shift.cashier?.email ??
                  '—'}
              </Text>
            </View>
            {shift.cashier?.phone && (
              <View style={s.kvRow}>
                <Text style={s.kvLabel}>Телефон кассира</Text>
                <Text style={s.kvValue}>{shift.cashier.phone}</Text>
              </View>
            )}
            {shift.notes ? (
              <View style={[s.kvRow, { alignItems: 'flex-start' }]}>
                <Text style={s.kvLabel}>Заметка</Text>
                <Text style={[s.kvValue, { maxWidth: '60%', textAlign: 'right' }]}>{shift.notes}</Text>
              </View>
            ) : null}
          </View>

          <View style={s.col}>
            <Text style={s.h2}>Итоги смены</Text>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Выручка</Text>
              <Text style={s.kvValueStrong}>{formatRub(shift.totals.revenue)}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Заказов всего</Text>
              <Text style={s.kvValue}>{shift.totals.ordersCount}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Оплачено</Text>
              <Text style={s.kvValue}>{shift.totals.paidOrdersCount}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Отменено</Text>
              <Text style={[s.kvValue, ...(shift.totals.cancelledCount > 0 ? [s.negValue] : [])]}>
                {shift.totals.cancelledCount > 0
                  ? `${shift.totals.cancelledCount} · ${formatRub(shift.totals.cancelledTotal)}`
                  : '0'}
              </Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Средний чек</Text>
              <Text style={s.kvValue}>{formatRub(shift.totals.avgCheck)}</Text>
            </View>
            <View style={s.kvRow}>
              <Text style={s.kvLabel}>Скидки</Text>
              <Text style={s.kvValue}>{formatRub(shift.totals.discountTotal)}</Text>
            </View>
          </View>
        </View>

        {/* ── Касса и движения ────────────────────────── */}
        <Text style={s.h2}>Касса (наличные): расчёт и сверка</Text>
        <View style={s.cashBox}>
          <View style={s.cashGrid}>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>Остаток на открытие</Text>
              <Text style={s.cashCellValue}>{formatRub(cash.opening)}</Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>+ Приход налом{cash.cashInCount ? ` · ${cash.cashInCount}` : ''}</Text>
              <Text style={[s.cashCellValue, s.posValue]}>{formatRub(cash.cashIn)}</Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>− Возвраты{cash.cashOutCount ? ` · ${cash.cashOutCount}` : ''}</Text>
              <Text style={[s.cashCellValue, ...(cash.cashOut > 0 ? [s.negValue] : [])]}>
                {cash.cashOut > 0 ? `−${formatRub(cash.cashOut)}` : formatRub(0)}
              </Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>
                − Закуп из кассы{cash.cashPayoutsCount ? ` · ${cash.cashPayoutsCount}` : ''}
              </Text>
              <Text style={[s.cashCellValue, ...(cashPayouts > 0 ? [s.negValue] : [])]}>
                {cashPayouts > 0 ? `−${formatRub(cashPayouts)}` : formatRub(0)}
              </Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>
                − Расходы из кассы{cash.cashExpensesCount ? ` · ${cash.cashExpensesCount}` : ''}
              </Text>
              <Text style={[s.cashCellValue, ...(cashExpenses > 0 ? [s.negValue] : [])]}>
                {cashExpenses > 0 ? `−${formatRub(cashExpenses)}` : formatRub(0)}
              </Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>= Должно быть в ящике</Text>
              <Text style={[s.cashCellValue, { fontWeight: 700 }]}>{formatRub(cash.expected)}</Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>Факт на закрытие</Text>
              <Text style={s.cashCellValue}>
                {cash.actual != null ? formatRub(cash.actual) : '—'}
              </Text>
            </View>
            <View style={s.cashCell}>
              <Text style={s.cashCellLabel}>Расхождение</Text>
              <Text style={[s.cashCellValue, { color: diffColor }]}>{diffLabel}</Text>
            </View>
          </View>
          <View style={{ marginTop: 6, borderTopWidth: 0.5, borderTopColor: C.line, paddingTop: 6 }}>
            <Text style={s.muted}>
              Безналичные поступления (карта/QR/перевод/онлайн): {formatRub(cash.nonCashIn)} · {cash.nonCashInCount} оплат
            </Text>
            {transferPayouts + transferExpenses > 0 ? (
              <Text style={s.muted}>
                Переводом с кассы (на ящик не влияет): закуп {formatRub(transferPayouts)} · расходы{' '}
                {formatRub(transferExpenses)}
              </Text>
            ) : null}
          </View>
        </View>

        {/* Лог движений по кассе */}
        {shift.cashMovements.length > 0 && (
          <>
            <Text style={s.h2}>Движения по кассе ({shift.cashMovements.length})</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '12%' }]}>Время</Text>
              <Text style={[s.th, { width: '14%' }]}>Заказ</Text>
              <Text style={[s.th, { width: '22%' }]}>Метод</Text>
              <Text style={[s.th, { width: '22%' }]}>Тип</Text>
              <Text style={[s.th, { width: '30%' }, s.right]}>Сумма</Text>
            </View>
            {shift.cashMovements.map((m, idx) => (
              <View
                key={m.id}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '12%' }]}>{formatTime(m.createdAt)}</Text>
                <Text style={[s.td, { width: '14%', fontWeight: 700 }]}>
                  {m.orderNumber != null ? `#${m.orderNumber}` : (m.note ?? '—')}
                  {m.acceptedBy ? `\nпринял ${m.acceptedBy}` : ''}
                </Text>
                <Text style={[s.td, { width: '22%' }]}>{paymentLabel[m.method] ?? m.method}</Text>
                <Text
                  style={[
                    s.td,
                    { width: '22%' },
                    m.direction === 'IN' ? s.posValue : s.negValue,
                  ]}
                >
                  {m.direction === 'IN'
                    ? '▼ Приход'
                    : m.kind === 'SUPPLY'
                      ? '▲ Поставка'
                      : m.kind === 'EXPENSE'
                        ? '▲ Расход'
                        : '▲ Возврат'}
                </Text>
                <Text
                  style={[
                    s.td,
                    { width: '30%', fontWeight: 700 },
                    s.right,
                    ...(m.direction === 'OUT' ? [s.negValue] : []),
                  ]}
                >
                  {m.direction === 'OUT' ? '−' : ''}
                  {formatRub(m.amount)}
                </Text>
              </View>
            ))}
          </>
        )}

        {/* Оплаты, принятые официантами с телефона: наличные у них, а не в ящике */}
        {shift.waiterCollections && shift.waiterCollections.length > 0 && (
          <>
            <Text style={s.h2}>Приняли официанты ({shift.waiterCollections.length})</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '40%' }]}>Официант</Text>
              <Text style={[s.th, { width: '14%' }, s.right]}>Оплат</Text>
              <Text style={[s.th, { width: '23%' }, s.right]}>Наличными</Text>
              <Text style={[s.th, { width: '23%' }, s.right]}>Безнал</Text>
            </View>
            {shift.waiterCollections.map((w, idx) => (
              <View
                key={w.waiterId ?? w.name}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '40%', fontWeight: 700 }]}>{w.name}</Text>
                <Text style={[s.td, { width: '14%' }, s.right]}>{w.count}</Text>
                <Text style={[s.td, { width: '23%', fontWeight: 700 }, s.right]}>{formatRub(w.cash)}</Text>
                <Text style={[s.td, { width: '23%' }, s.right]}>{formatRub(w.cashless)}</Text>
              </View>
            ))}
          </>
        )}

        {/* Выплаты из кассы (за что оплачено) */}
        {shift.payouts && shift.payouts.length > 0 && (
          <>
            <Text style={s.h2}>Выплаты из кассы ({shift.payouts.length})</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '12%' }]}>Время</Text>
              <Text style={[s.th, { width: '58%' }]}>Поставщик / состав</Text>
              <Text style={[s.th, { width: '30%' }, s.right]}>Сумма</Text>
            </View>
            {shift.payouts.map((p, idx) => (
              <View
                key={p.id}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '12%' }]}>{formatTime(p.createdAt)}</Text>
                <Text style={[s.td, { width: '58%' }]}>
                  {p.supplierName ?? 'Поставка'}
                  {p.items.length
                    ? ` — ${p.items.map((it) => `${it.name} ${it.quantity} ${it.unit}`).join(', ')}`
                    : ''}
                  {p.comment ? ` · ${p.comment}` : ''}
                </Text>
                <Text style={[s.td, { width: '30%', fontWeight: 700 }, s.right, s.negValue]}>
                  −{formatRub(p.paidAmount)}
                </Text>
              </View>
            ))}
          </>
        )}

        {/* Расходы заведения, проведённые через кассу смены */}
        {shift.expenses && shift.expenses.length > 0 && (
          <>
            <Text style={s.h2}>Расходы смены ({shift.expenses.length})</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '12%' }]}>Время</Text>
              <Text style={[s.th, { width: '46%' }]}>Статья / комментарий</Text>
              <Text style={[s.th, { width: '22%' }]}>Способ</Text>
              <Text style={[s.th, { width: '20%' }, s.right]}>Сумма</Text>
            </View>
            {shift.expenses.map((e, idx) => (
              <View
                key={e.id}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '12%' }]}>{formatTime(e.createdAt)}</Text>
                <Text style={[s.td, { width: '46%' }]}>
                  {e.category?.name ?? 'Без статьи'}
                  {e.comment ? ` — ${e.comment}` : ''}
                  {e.addedAfterClose ? ' (внесён после закрытия)' : ''}
                </Text>
                <Text style={[s.td, { width: '22%' }]}>
                  {e.method === 'KASSA_CASH' ? 'Наличные из кассы' : 'Перевод с кассы'}
                </Text>
                <Text style={[s.td, { width: '20%', fontWeight: 700 }, s.right, s.negValue]}>
                  −{formatRub(e.amount)}
                </Text>
              </View>
            ))}
          </>
        )}

        {/* Позиции, снятые кассиром с заказов до оплаты */}
        {shift.itemChanges && shift.itemChanges.length > 0 && (
          <>
            <Text style={s.h2}>
              Снятые позиции ({shift.totals.removedItemsQty ?? shift.itemChanges.length} шт ·{' '}
              {formatRub(shift.totals.removedItemsAmount ?? 0)})
            </Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '12%' }]}>Время</Text>
              <Text style={[s.th, { width: '16%' }]}>Заказ</Text>
              <Text style={[s.th, { width: '34%' }]}>Позиция</Text>
              <Text style={[s.th, { width: '18%' }]}>Кто снял</Text>
              <Text style={[s.th, { width: '20%' }, s.right]}>Сумма</Text>
            </View>
            {shift.itemChanges.map((c, idx) => (
              <View
                key={c.id}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '12%' }]}>{formatTime(c.createdAt)}</Text>
                <Text style={[s.td, { width: '16%' }]}>
                  {c.orderNumber != null ? `#${c.orderNumber}` : '—'}
                  {c.tableNumber != null ? ` · стол ${c.tableNumber}` : ''}
                </Text>
                <Text style={[s.td, { width: '34%' }]}>
                  {c.dishName} — {c.qtyBefore} → {c.qtyAfter}
                  {c.kind === 'REMOVED' ? ' (удалена)' : ''}
                  {c.reason ? `\n${removalReasonLine(c)}` : ''}
                </Text>
                <Text style={[s.td, { width: '18%' }]}>
                  {c.actorName ? `${c.actorName} (официант)` : (c.cashierName ?? '—')}
                </Text>
                <Text style={[s.td, { width: '20%', fontWeight: 700 }, s.right, s.negValue]}>
                  −{formatRub(c.amount)}
                </Text>
              </View>
            ))}
          </>
        )}

        {/* ── По методам оплаты / По разделам ─────────── */}
        <View style={s.twoCol}>
          <View style={s.col}>
            <Text style={s.h2}>По методам оплаты</Text>
            {shift.paymentBreakdown.filter((p) => p.amount > 0 || p.count > 0).length === 0 ? (
              <Text style={s.muted}>Оплат нет</Text>
            ) : (
              shift.paymentBreakdown
                .filter((p) => p.amount > 0 || p.count > 0)
                .map((p) => (
                  <View key={p.method} style={s.kvRowBordered}>
                    <Text style={s.kvValue}>{paymentLabel[p.method] ?? p.method}</Text>
                    <Text style={s.kvValueStrong}>
                      <Text style={s.muted}>{p.count} шт · </Text>
                      {formatRub(p.amount)}
                    </Text>
                  </View>
                ))
            )}
          </View>
          <View style={s.col}>
            <Text style={s.h2}>По разделам</Text>
            {/* Разделы заведения как есть; для старых отчётов — прежние 3 ключа. */}
            {(shift.sectionRows
              ?? (Object.entries(shift.sections) as Array<[string, { quantity: number; total: number }]>)
                .map(([sec, v]) => ({ name: sectionLabel[sec] ?? sec, ...v }))
            ).map((row) => (
              <View key={row.name} style={s.kvRowBordered}>
                <Text style={s.kvValue}>{row.name}</Text>
                <Text style={s.kvValueStrong}>
                  <Text style={s.muted}>{row.quantity} шт · </Text>
                  {formatRub(row.total)}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Источники / Статусы ─────────────────────── */}
        <View style={s.twoCol}>
          <View style={s.col}>
            <Text style={s.h2}>Источники заказов</Text>
            {shift.sources.map((src) => (
              <View key={src.source} style={s.kvRowBordered}>
                <Text style={s.kvValue}>{sourceLabel[src.source] ?? src.source}</Text>
                <Text style={s.kvValue}>
                  <Text style={s.muted}>{src.count} шт · </Text>
                  {formatRub(src.revenue)}
                </Text>
              </View>
            ))}
          </View>
          <View style={s.col}>
            <Text style={s.h2}>Статусы заказов</Text>
            {shift.statuses.map((st) => (
              <View key={st.status} style={s.kvRowBordered}>
                <Text style={s.kvValue}>{statusLabel[st.status] ?? st.status}</Text>
                <Text style={s.kvValue}>
                  <Text style={s.muted}>{st.count} шт · </Text>
                  {formatRub(st.revenue)}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Активность по часам ─────────────────────── */}
        {shift.hours.length > 0 && (
          <>
            <Text style={s.h2}>Активность по часам</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '25%' }]}>Час</Text>
              <Text style={[s.th, { width: '25%' }, s.right]}>Заказов</Text>
              <Text style={[s.th, { width: '50%' }, s.right]}>Выручка</Text>
            </View>
            {shift.hours.map((h, idx) => (
              <View
                key={h.hour}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '25%' }]}>{String(h.hour).padStart(2, '0')}:00</Text>
                <Text style={[s.td, { width: '25%' }, s.right]}>{h.count}</Text>
                <Text style={[s.td, { width: '50%' }, s.right]}>{formatRub(h.revenue)}</Text>
              </View>
            ))}
          </>
        )}

        {footer}
      </Page>

      {/* ── Все заказы смены — отдельная страница: шапка таблицы `fixed`
          повторяется на каждом листе, длинная смена не обрезается. ── */}
      {shift.orders.length > 0 && (
        <Page size="A4" style={s.page}>
          <View fixed>
            <Text style={s.h2}>Все заказы смены ({shift.orders.length})</Text>
            <View style={s.tableHeader}>
              <Text style={[s.th, { width: '8%' }]}>№</Text>
              <Text style={[s.th, { width: '10%' }]}>Время</Text>
              <Text style={[s.th, { width: '14%' }]}>Статус</Text>
              <Text style={[s.th, { width: '18%' }]}>Источник</Text>
              <Text style={[s.th, { width: '10%' }]}>Стол</Text>
              <Text style={[s.th, { width: '18%' }]}>Клиент</Text>
              <Text style={[s.th, { width: '10%' }, s.right]}>Скидка</Text>
              <Text style={[s.th, { width: '12%' }, s.right]}>Итого</Text>
            </View>
          </View>
          {shift.orders.map((o, idx) => (
              <View
                key={o.id}
                style={[s.tableRow, ...(idx % 2 === 1 ? [s.tableRowZebra] : [])]}
                wrap={false}
              >
                <Text style={[s.td, { width: '8%', fontWeight: 700 }]}>#{o.number}</Text>
                <Text style={[s.td, { width: '10%' }]}>{formatTime(o.createdAt)}</Text>
                <Text
                  style={[
                    s.td,
                    { width: '14%' },
                    ...(o.status === 'CANCELLED' ? [s.negValue] : o.status === 'COMPLETED' ? [s.posValue] : []),
                  ]}
                >
                  {statusLabel[o.status] ?? o.status}
                </Text>
                <Text style={[s.td, { width: '18%' }]}>{sourceLabel[o.source] ?? o.source}</Text>
                <Text style={[s.td, { width: '10%' }]}>
                  {o.table ? `№${o.table.number}` : '—'}
                </Text>
                <Text style={[s.td, { width: '18%' }]}>
                  {o.client?.name ?? o.client?.phone ?? '—'}
                </Text>
                <Text style={[s.td, { width: '10%' }, s.right]}>
                  {o.discount > 0 ? formatRub(o.discount) : '—'}
                </Text>
                <Text style={[s.td, { width: '12%', fontWeight: 700 }, s.right]}>
                  {formatRub(o.total)}
                </Text>
              </View>
            ))}
          {footer}
        </Page>
      )}
    </Document>
  );
}
